import * as React from "react";
import { FC, useCallback, useMemo } from "react";

import { Container, Tab, TabPanel, Tabs, TabsContextProvider, Text } from "@costar/theme-ui";
import { CentralPreferenceCulture } from "@costar-suite/preferences-mapping";

import { RichIcu, useI18N } from "../../../../i18n/react-i18n";

import SelectorFooter from "./selector-footer";
import SelectorGrid from "./selector-grid";

import { client } from "$graphql/client/client-side-provider";
import { CultureOption, CurrencyOption, Maybe } from "$graphql/core";
import { SET_PREFERENCES_MUTATION } from "$graphql/core/queries/set-preferences";

type PreferenceSelectorContentProps = {
    languageOptions: Maybe<readonly Maybe<CultureOption>[]> | undefined;
    benchmarkCultures: Maybe<readonly Maybe<CultureOption>[]> | undefined;
    currencyOptions: Maybe<readonly Maybe<CurrencyOption>[]> | undefined;
    selectedLocale?: CultureOption | CurrencyOption | null;
    setSelectedLocaleState: (value: React.SetStateAction<CultureOption | null>) => void;
    selectedCurrency?: Maybe<CurrencyOption> | null;
    setSelectedCurrencyState: (value: React.SetStateAction<Maybe<CurrencyOption> | null>) => void;
    centralPrefUrl: string;
    isBenchmarkingSubscriber?: boolean;
};

const PreferenceSelectorContent: FC<PreferenceSelectorContentProps> = props => {
    const {
        languageOptions,
        currencyOptions,
        benchmarkCultures,
        selectedLocale,
        selectedCurrency,
        centralPrefUrl,
        isBenchmarkingSubscriber,
        setSelectedLocaleState,
        setSelectedCurrencyState,
    } = props;
    const culturalResources = useI18N()!;

    const handleCurrencyOptionSelect = useCallback(
        (optionId: Maybe<number> | undefined) => {
            const input = {
                preferences: [
                    {
                        identifier: "Currency",
                        value: optionId,
                    },
                ],
            };
            const selectedCurrency = currencyOptions?.find(option => option?.id === optionId) ?? null;
            setSelectedCurrencyState(selectedCurrency);

            fetch("/bff2/log/news/ev", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    0: {
                        eventDetails: {
                            event: "BUTTON_CLICK",
                            area: "UNIVERSAL_HEADER",
                            clickedButton: "PREFERENCE_CURRENCY",
                            referer: window.location.href,
                            detailsJson: {
                                currency: selectedCurrency?.value || "",
                            },
                        },
                        pathName: window.location.pathname,
                    },
                }),
            });

            client
                .mutate({
                    mutation: SET_PREFERENCES_MUTATION,
                    variables: { input },
                })
                .then(res => {
                    if (res.data?.setPreferences?.success) {
                        if (typeof window !== "undefined" && window.location) {
                            window.location.reload();
                        }
                    }
                });
        },
        [currencyOptions, setSelectedCurrencyState]
    );

    const handleLocaleOptionSelect = useCallback(
        (optionId: Maybe<number> | undefined) => {
            const input = {
                preferences: [
                    {
                        identifier: "Culture",
                        value: optionId,
                    },
                    {
                        identifier: "STROverrideCulture",
                        value: optionId,
                    },
                ],
            };
            const selectedLocale = languageOptions?.find(option => option?.id === optionId) ?? null;
            setSelectedLocaleState(selectedLocale);

            fetch("/bff2/log/news/ev", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    0: {
                        eventDetails: {
                            event: "BUTTON_CLICK",
                            area: "UNIVERSAL_HEADER",
                            clickedButton: "PREFERENCE_LANGUAGE",
                            referer: window.location.href,
                            detailsJson: {
                                language: selectedLocale?.value || "",
                            },
                        },
                        pathName: window.location.pathname,
                    },
                }),
            });

            client
                .mutate({
                    mutation: SET_PREFERENCES_MUTATION,
                    variables: { input },
                })
                .then(res => {
                    if (res.data?.setPreferences?.success) {
                        if (typeof window !== "undefined" && window.location) {
                            window.location.reload();
                        }
                    }
                });
        },
        [languageOptions, setSelectedLocaleState]
    );

    const benchmarkOptionSelect = React.useCallback(
        (optionId: Maybe<number> | undefined) => {
            const input = {
                preferences: [
                    {
                        identifier: "STROverrideCulture",
                        value: optionId,
                    },
                    {
                        identifier: "Culture",
                        value: CentralPreferenceCulture.EnglishUnitedStates,
                    },
                ],
            };
            const selectedLocale = benchmarkCultures?.find(option => option?.id === optionId) ?? null;
            setSelectedLocaleState(selectedLocale);

            fetch("/bff2/log/news/ev", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    0: {
                        eventDetails: {
                            event: "BUTTON_CLICK",
                            area: "UNIVERSAL_HEADER",
                            clickedButton: "PREFERENCE_BENCHMARK_LANGUAGE",
                            referer: window.location.href,
                            detailsJson: {
                                language: selectedLocale?.value || "",
                            },
                        },
                        pathName: window.location.pathname,
                    },
                }),
            });

            client
                .mutate({
                    mutation: SET_PREFERENCES_MUTATION,
                    variables: { input },
                })
                .then(res => {
                    if (res.data?.setPreferences?.success) {
                        if (typeof window !== "undefined" && window.location) {
                            window.location.reload();
                        }
                    }
                });
        },
        [benchmarkCultures, setSelectedLocaleState]
    );

    const benchmarkSection = useMemo(
        () => ({
            title: culturalResources.preferences.benchmark.title(),
            options: benchmarkCultures,
            handleOptionSelect: benchmarkOptionSelect,
        }),
        [benchmarkCultures, culturalResources.preferences.benchmark, benchmarkOptionSelect]
    );

    return (
        <Container
            flexDirection="column"
            style={{
                width: "100%",
                minWidth: "848px",
                maxHeight: "1200px",
                overflow: "auto",
            }}
        >
            <TabsContextProvider>
                <Tabs
                    style={{
                        borderBottom: "1px solid #e0e0e0",
                        backgroundColor: "#FCFDFD",
                    }}
                >
                    <Tab
                        key={"language-tab"}
                        automation-id="language-tab"
                        style={{
                            color: "#666",
                            fontSize: "14px",
                            fontWeight: "medium",
                            border: "none",
                            backgroundColor: "transparent",
                            cursor: "pointer",
                            marginLeft: "0px",
                            marginRight: "16px",
                        }}
                    >
                        <Text fontSize="sm" color="#000000">
                            {culturalResources.preferences.tab.language()}
                        </Text>
                    </Tab>
                    <Tab
                        key={"currency-tab"}
                        automation-id="currency-tab"
                        style={{
                            color: "#666",
                            fontSize: "14px",
                            fontWeight: "medium",
                            border: "none",
                            backgroundColor: "transparent",
                            cursor: "pointer",
                        }}
                    >
                        <Text fontSize="sm" color="#000000">
                            {culturalResources.preferences.tab.currency()}
                        </Text>
                    </Tab>
                </Tabs>

                <TabPanel key={"language-panel"} value={0} automation-id="language-panel" style={{ padding: 0 }}>
                    <SelectorGrid
                        title={culturalResources.preferences.language.title()}
                        options={languageOptions}
                        handleOptionSelect={handleLocaleOptionSelect}
                        benchmarkSection={isBenchmarkingSubscriber ? benchmarkSection : undefined}
                        selectedOption={selectedLocale}
                    />
                    <SelectorFooter
                        centralPrefUrl={centralPrefUrl}
                        customizeLinkText={culturalResources.preferences.footer.customizeLink()}
                    />
                </TabPanel>
                <TabPanel key={"currency-panel"} value={1} automation-id="currency-panel" style={{ padding: 0 }}>
                    <SelectorGrid
                        title={culturalResources.preferences.currency.title()}
                        options={currencyOptions}
                        handleOptionSelect={handleCurrencyOptionSelect}
                        selectedOption={selectedCurrency}
                        tooltipText={
                            <RichIcu
                                key="culture-tooltip"
                                icuContent={culturalResources.preferences.currency.tooltipText()}
                            />
                        }
                    />
                    <SelectorFooter
                        centralPrefUrl={centralPrefUrl}
                        customizeLinkText={culturalResources.preferences.footer.customizeLink()}
                    />
                </TabPanel>
            </TabsContextProvider>
        </Container>
    );
};

export default React.memo(PreferenceSelectorContent);
